import { createContext, useContext, useEffect, useState } from 'react'
import { findCourse } from './data'

const GameContext = createContext(undefined)

export function useGame() {
  const ctx = useContext(GameContext)
  if (!ctx) throw new Error('useGame must be used within a GameProvider')
  return ctx
}

const INITIAL_STATE = {
  course: null,
  players: [],
  currentHole: 1,
  holes: [],
}

const STORAGE_KEY = 'minigolf:game-state'
const STORAGE_VERSION = 1

function loadPersistedState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return INITIAL_STATE
    const parsed = JSON.parse(raw)
    if (parsed.version !== STORAGE_VERSION) return INITIAL_STATE
    const course = findCourse(parsed.courseId)
    if (!course || !Array.isArray(parsed.holes) || !Array.isArray(parsed.players)) {
      return INITIAL_STATE
    }
    return {
      course,
      players: parsed.players,
      currentHole: typeof parsed.currentHole === 'number' ? parsed.currentHole : 1,
      holes: parsed.holes,
    }
  } catch {
    return INITIAL_STATE
  }
}

export function GameProvider({ children }) {
  const [gameState, setGameState] = useState(loadPersistedState)

  useEffect(() => {
    if (!gameState.course) return
    const { course, ...rest } = gameState
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...rest, courseId: course.id, version: STORAGE_VERSION })
    )
  }, [gameState])

  const setCourse = (course) => {
    const holes = Array.from({ length: course.holes }, (_, i) => {
      const holeData = course.holesData?.[i]
      return {
        number: i + 1,
        par: holeData?.par ?? 3,
        scores: [],
      }
    })
    setGameState((prev) => ({
      ...prev,
      course,
      currentHole: 1,
      holes,
    }))
  }

  const addPlayer = (player) => {
    setGameState((prev) => ({
      ...prev,
      players: [...prev.players, player],
    }))
  }

  const removePlayer = (id) => {
    setGameState((prev) => ({
      ...prev,
      players: prev.players.filter((p) => p.id !== id),
    }))
  }

  const updatePlayerColor = (id, color) => {
    setGameState((prev) => ({
      ...prev,
      players: prev.players.map((p) =>
        p.id === id ? { ...p, selectedColor: color } : p
      ),
    }))
  }

  const updateScore = (holeNumber, playerId, strokes) => {
    setGameState((prev) => {
      const holes = [...prev.holes]
      const idx = holeNumber - 1
      const hole = holes[idx]
      const scoreIdx = hole.scores.findIndex((s) => s.playerId === playerId)
      if (scoreIdx >= 0) {
        hole.scores[scoreIdx].strokes = strokes
      } else {
        hole.scores.push({ playerId, strokes })
      }
      return { ...prev, holes }
    })
  }

  const setCurrentHole = (hole) => {
    setGameState((prev) => ({ ...prev, currentHole: hole }))
  }

  const resetGame = () => {
    sessionStorage.removeItem(STORAGE_KEY)
    setGameState(INITIAL_STATE)
  }

  return (
    <GameContext.Provider
      value={{
        gameState,
        setCourse,
        addPlayer,
        removePlayer,
        updatePlayerColor,
        updateScore,
        setCurrentHole,
        resetGame,
      }}
    >
      {children}
    </GameContext.Provider>
  )
}